import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { format } from "date-fns";
import { customLocaleText } from "../../utils/objects/CustomLocaleTextTables";
import Moment from "moment";

function ToolbarSinExport() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );
}

const DocumentsDialog = ({ open, onClose, setManifestData, userId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const toMillis = (v) => {
    if (!v) return 0;
    try {
      if (typeof v?.toDate === "function") return v.toDate().getTime(); // Timestamp
      if (typeof v?.seconds === "number") {
        return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
      }
      if (v instanceof Date) return v.getTime();
      if (typeof v === "number") return v;
    } catch (_) {}
    return 0;
  };

  const columns = [
    { field: "name", headerName: "Nombre", width: 300 },
    {
      field: "date",
      headerName: "Fecha",
      width: 180,

      // Correcto y funciona bien Devuelve un string ya formateado (YYYY-MM-DD HH:mm:ss)
      valueGetter: (params) => {
        const v = params;
        if (!v) return "N/A";

        try {
          // Firestore Timestamp clásico
          if (typeof v?.toDate === "function") {
            return Moment(v.toDate()).format("YYYY-MM-DD HH:mm:ss");
          }
          // Objeto {seconds, nanoseconds}
          if (typeof v?.seconds === "number") {
            const ms =
              v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
            return Moment(ms).format("YYYY-MM-DD HH:mm:ss");
          }
          // Date nativa
          if (v instanceof Date) {
            return Moment(v).format("YYYY-MM-DD HH:mm:ss");
          }
          // epoch en ms
          if (typeof v === "number") {
            return Moment(v).format("YYYY-MM-DD HH:mm:ss");
          }
        } catch (_) {
          /* ignore */
        }

        return "N/A";
      },

      // Ordena usando el valor crudo de row.date  hay un error no ordena
      sortComparator: (v1, v2, cellParams1, cellParams2) => {
        const a = cellParams1?.row?.dateMs ?? 0;
        const b = cellParams2?.row?.dateMs ?? 0;
        return b - a; // recientes arriba
      },
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleEdit(params.row.id)}
          >
            Editar
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={() => handleDelete(params.row.id)}
          >
            Eliminar
          </Button>
        </Box>
      ),
    },
  ];

  // helper: convierte cualquier forma de fecha (Timestamp, {seconds}, Date, string) a Date o null
  const toJSDate = (raw) => {
    if (!raw) return null;
    if (raw instanceof Date) return raw;
    if (typeof raw?.toDate === "function") return raw.toDate(); // Firestore Timestamp
    if (typeof raw?.seconds === "number") return new Date(raw.seconds * 1000); // {seconds, nanoseconds}
    if (typeof raw === "number") return new Date(raw); // epoch ms
    if (typeof raw === "string") {
      const d = new Date(raw);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const colRef = collection(db, "xpress_manifestDevolutions");

      // superadmin => ve todos; si no, solo los del usuario
      const q =
        userId === process.env.REACT_APP_SUPERADMIN
          ? query(colRef, where("isDeleted", "==", false))
          : userId
          ? query(
              colRef,
              where("isDeleted", "==", false),
              where("createBy", "==", userId)
            )
          : query(colRef, where("isDeleted", "==", false));

      const qs = await getDocs(q);

      const docs = qs.docs
        .map((d) => {
          const data = d.data();
          const dateJS = toJSDate(data?.date); // <-- normalizamos aquí
          const dateMs = toMillis(data?.date);
          return {
            id: d.id,
            name: data?.title || "Sin nombre",
            date: dateJS, // <-- ya es Date (no Timestamp)
            dateMs,
            ...data,
          };
        })
        .sort((a, b) => b.dateMs - a.dateMs);

      setDocuments(docs);
    } catch (err) {
      console.error("Error al cargar documentos:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchDocuments();
  }, [open, userId]);

  useEffect(() => {
    if (open) fetchDocuments();
  }, [open]);

  const handleEdit = (id) => {
    const selected = documents.find((d) => d.id === id);
    const data = selected; // en vez de selected._raw
    setManifestData({
      id: selected.id,
      companyId: data.companyId || "falabella",
      createBy: data.createBy || "userID",
      date: data.date || Timestamp.now(),
      isActive: data.isActive !== undefined ? data.isActive : true,
      isDeleted: false,
      orders: Array.isArray(data.orders) ? data.orders : [],
      title: data.title || selected.name || "Sin título",
      dayID: data.dayID,
      print: !!data.print,
      versions: typeof data.versions === "number" ? data.versions : 0,
      historyLogs: Array.isArray(data.historyLogs) ? data.historyLogs : [],
    });

    onClose();
  };

  const handleDelete = async (id) => {
    try {
      await setDoc(
        doc(db, "xpress_manifestDevolutions", id),
        { isDeleted: true, update: Timestamp.now() },
        { merge: true }
      );
      setDocuments((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      console.error("Error al eliminar documento:", err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Documentos de Devoluciones</DialogTitle>
      <DialogContent>
        <Box sx={{ height: 520, width: "100%" }}>
          <DataGrid
            rows={documents}
            columns={columns}
            getRowId={(row) => row.id}
            loading={loading}
            pagination
            pageSizeOptions={[5, 10, 20]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            localeText={customLocaleText}
            slots={{ toolbar: ToolbarSinExport }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentsDialog;
